import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// Components
import Topbar from '../main/Topbar';
import ProjectSidebar from '../../components/ProjectSidebar';

// Utils
import { getProjectCode } from '../../utils/auth';
import { ProjectDetail } from '../../utils/commonUtils';
import { CategoryGroupFormUtils } from '../../utils/shopCategoryGroupCommon';
import {
  getCategoryGroupDetail,
  getShopCountByTreeCodes,
  CategoryGroupDetail,
  CategoryGroupTreeNode as ApiCategoryGroupTreeNode,
  getTreeShops,
  updateShopInTree
} from '../../utils/shopCategoryGroup';
import { getShopList, Shop, ShopListRequest } from '../../utils/shop';
import { generateBetween } from '../../utils/fractionalIndex';
import {
  ArrowLeft,
  ChevronRight,
  ChevronDown,
  X,
  Store,
  FolderTree,
  GripVertical
} from 'lucide-react';

// 카테고리/그룹 트리 노드 인터페이스 (UI용)
interface CategoryGroupNode {
  code: string;
  title: string;
  brandCode?: string;
  parentCode?: string;
  depth: number;
  sortOrder?: number;
  status?: string;
  type?: string;
  children?: CategoryGroupNode[];
}

// API 트리 노드(sub) -> UI 트리 노드(children) 변환
const convertApiTreeToNodes = (nodes: ApiCategoryGroupTreeNode[] | undefined): CategoryGroupNode[] => {
  if (!nodes || !Array.isArray(nodes) || nodes.length === 0) return [];
  return nodes.map((node, index) => ({
    code: node.code,
    title: node.title,
    depth: node.depth ?? 0,
    status: node.status,
    sortOrder: index,
    children: node.sub && node.sub.length > 0 ? convertApiTreeToNodes(node.sub) : undefined
  }));
};

// 카테고리/그룹 매장 매핑 (로컬 상태, sortOrder는 fractional index)
interface CategoryGroupShop {
  categoryGroupCode: string;
  shopCode: string;
  order: number;
  sortOrder: string;
}

const ShopCategoryGroupShopConfig: React.FC = () => {
  const navigate = useNavigate();
  const { categoryGroupCode } = useParams<{ categoryGroupCode: string }>();

  // UI state
  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState('shop-category-group-list');

  // 카테고리/그룹 트리 상태
  const [treeData, setTreeData] = useState<CategoryGroupNode[]>([]);
  const [selectedCategoryGroupCode, setSelectedCategoryGroupCode] = useState<string | null>(null);
  const [expandedCodes, setExpandedCodes] = useState<Set<string>>(new Set());
  const [isLoadingTree, setIsLoadingTree] = useState(false);
  const [treeNodeShopCounts, setTreeNodeShopCounts] = useState<Record<string, number>>({});
  const [treeCountsLoaded, setTreeCountsLoaded] = useState(false);

  // 매장 상태
  const [allShops, setAllShops] = useState<Shop[]>([]);
  const [isLoadingShops, setIsLoadingShops] = useState(false);
  const [shopListError, setShopListError] = useState<string | null>(null);

  // 드래그앤드랍 상태
  const [draggedShop, setDraggedShop] = useState<Shop | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dropZoneActive, setDropZoneActive] = useState(false);
  const [dropInsertIndex, setDropInsertIndex] = useState<number | null>(null);

  // 카테고리/그룹별 매장 목록 (노드 선택 시 API 로드, 드롭·삭제 시 즉시 API 호출로 반영)
  const [categoryGroupShops, setCategoryGroupShops] = useState<Map<string, CategoryGroupShop[]>>(new Map());
  const [isLoadingNodeShops, setIsLoadingNodeShops] = useState(false);
  const [apiBusy, setApiBusy] = useState(false);

  // Project state
  const [projectDetail, setProjectDetail] = useState<ProjectDetail | null>(null);
  const [selectedCategoryGroup, setSelectedCategoryGroup] = useState<CategoryGroupDetail | null>(null);

  CategoryGroupFormUtils.useMobileSidebarResize(setIsMobileSidebarOpen);

  useEffect(() => {
    const projectInfo = CategoryGroupFormUtils.loadProjectDetail();
    if (projectInfo) {
      setProjectDetail(projectInfo);
    }
  }, []);

  useEffect(() => {
    const fetchCategoryGroupData = async () => {
      if (!projectDetail || !categoryGroupCode) {
        return;
      }

      try {
        setIsLoadingTree(true);
        const projectCode = projectDetail.code || getProjectCode();
        if (!projectCode) {
          return;
        }

        const categoryGroupDetail = await getCategoryGroupDetail(projectCode, categoryGroupCode);
        setSelectedCategoryGroup(categoryGroupDetail);

        const initialTreeData = convertApiTreeToNodes(categoryGroupDetail.tree);
        setTreeData(initialTreeData);

        const collectAllCodes = (nodes: CategoryGroupNode[]): string[] => {
          const codes: string[] = [];
          nodes.forEach(node => {
            codes.push(node.code);
            if (node.children && node.children.length > 0) {
              codes.push(...collectAllCodes(node.children));
            }
          });
          return codes;
        };
        setExpandedCodes(new Set(collectAllCodes(initialTreeData)));

        setTreeCountsLoaded(false);
        const nodeCodes = collectAllCodes(initialTreeData);
        if (nodeCodes.length > 0) {
          const treeCodes = nodeCodes.map((code) => `${categoryGroupCode}:${code}`);
          try {
            const counts = await getShopCountByTreeCodes(projectCode, treeCodes);
            setTreeNodeShopCounts(counts);
          } catch (countErr) {
            console.error('노드별 매장 수 조회 오류:', countErr);
            setTreeNodeShopCounts({});
          }
        } else {
          setTreeNodeShopCounts({});
        }
        setTreeCountsLoaded(true);
      } catch (err) {
        console.error('카테고리/그룹 정보 조회 오류:', err);
        setSelectedCategoryGroup({
          code: categoryGroupCode,
          title: categoryGroupCode,
          status: 'ACTIVE',
          type: 'CATEGORY'
        } as CategoryGroupDetail);
        setTreeData([]);
        setTreeNodeShopCounts({});
      } finally {
        setTreeCountsLoaded(true);
        setIsLoadingTree(false);
      }
    };

    if (projectDetail) {
      fetchCategoryGroupData();
    }
  }, [projectDetail, categoryGroupCode]);

  // 전체 매장 목록 조회 (현재 카테고리/그룹 상세 로드 후, 해당 브랜드로 필터)
  useEffect(() => {
    const fetchShops = async () => {
      if (!projectDetail || !categoryGroupCode || selectedCategoryGroup == null) {
        return;
      }

      try {
        setIsLoadingShops(true);
        setShopListError(null);

        const projectCode = projectDetail.code || getProjectCode();
        if (!projectCode) {
          throw new Error('프로젝트 코드가 없습니다.');
        }

        const request: ShopListRequest = {
          page: 0,
          size: 1000,
          ...(selectedCategoryGroup.brandCode && { brandCode: selectedCategoryGroup.brandCode })
        };

        const response = await getShopList(projectCode, request);
        setAllShops(response.shops || []);
      } catch (err) {
        console.error('매장 목록 조회 오류:', err);
        setShopListError(err instanceof Error ? err.message : '매장 목록을 불러오는 중 오류가 발생했습니다.');
        setAllShops([]);
      } finally {
        setIsLoadingShops(false);
      }
    };

    fetchShops();
  }, [projectDetail, categoryGroupCode, selectedCategoryGroup]);

  // 선택한 노드의 매장 목록 조회 (tree-shops API)
  useEffect(() => {
    const fetchNodeShops = async () => {
      if (!projectDetail || !categoryGroupCode || !selectedCategoryGroupCode) {
        return;
      }

      const projectCode = projectDetail.code || getProjectCode();
      if (!projectCode) return;

      const treeCode = `${categoryGroupCode}:${selectedCategoryGroupCode}`;

      try {
        setIsLoadingNodeShops(true);
        const res = await getTreeShops(projectCode, [treeCode]);
        console.log('[tree-shops] 요청 treeCode:', treeCode, '응답:', res);
        const list: CategoryGroupShop[] = (res.shops || []).map((item, index) => ({
          categoryGroupCode: selectedCategoryGroupCode,
          shopCode: item.shopCode,
          order: index,
          sortOrder: item.sortOrder ?? ''
        }));
        setCategoryGroupShops(prev => new Map(prev).set(selectedCategoryGroupCode, list));
      } catch (err) {
        console.error('노드 매장 목록 조회 오류:', err);
        setCategoryGroupShops(prev => new Map(prev).set(selectedCategoryGroupCode, []));
      } finally {
        setIsLoadingNodeShops(false);
      }
    };

    fetchNodeShops();
  }, [projectDetail, categoryGroupCode, selectedCategoryGroupCode]);

  const handleCategoryGroupToggle = (code: string) => {
    setExpandedCodes(prev => {
      const next = new Set(prev);
      if (next.has(code)) {
        next.delete(code);
      } else {
        next.add(code);
      }
      return next;
    });
  };

  const handleCategoryGroupSelect = (code: string) => {
    setSelectedCategoryGroupCode(code);
  };

  const handleDragStartFromAll = (shop: Shop) => (e: React.DragEvent) => {
    setDraggedShop(shop);
    setDraggedIndex(null);
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleDragStartFromCategory = (shop: Shop, index: number) => (e: React.DragEvent) => {
    setDraggedShop(shop);
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setDraggedShop(null);
    setDraggedIndex(null);
    setDropZoneActive(false);
    setDropInsertIndex(null);
  };

  const getTreeCode = (): string | null => {
    if (!selectedCategoryGroupCode || !categoryGroupCode) return null;
    return `${categoryGroupCode}:${selectedCategoryGroupCode}`;
  };

  const handleDropFromRight = async (insertIndex: number, e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDropZoneActive(false);
    setDropInsertIndex(null);

    if (!selectedCategoryGroupCode) {
      alert('카테고리/그룹을 먼저 선택해 주세요.');
      return;
    }
    if (!draggedShop || draggedIndex !== null || !projectDetail) return;

    const projectCode = projectDetail.code || getProjectCode();
    if (!projectCode) return;

    const currentShops = categoryGroupShops.get(selectedCategoryGroupCode) || [];
    const treeCode = getTreeCode();
    if (!treeCode) return;

    if (currentShops.some(cp => cp.shopCode === draggedShop.code)) {
      alert('이미 추가된 매장입니다.');
      return;
    }

    const prev = insertIndex > 0 ? currentShops[insertIndex - 1] : null;
    const next = insertIndex < currentShops.length ? currentShops[insertIndex] : null;
    const sortOrder = generateBetween(prev?.sortOrder ?? null, next?.sortOrder ?? null);

    try {
      setApiBusy(true);
      await updateShopInTree(projectCode, draggedShop.code, {
        operation: 'update',
        treeCode,
        sortOrder
      });
      const newEntry: CategoryGroupShop = {
        categoryGroupCode: selectedCategoryGroupCode,
        shopCode: draggedShop.code,
        order: insertIndex,
        sortOrder
      };
      const newShops = [...currentShops];
      newShops.splice(insertIndex, 0, newEntry);
      const withOrder = newShops.map((cp, index) => ({ ...cp, order: index }));
      setCategoryGroupShops(prev => new Map(prev).set(selectedCategoryGroupCode, withOrder));
    } catch (err) {
      console.error('매장 추가 실패:', err);
      alert(err instanceof Error ? err.message : '매장 추가에 실패했습니다.');
    } finally {
      setApiBusy(false);
    }
  };

  const handleDropToCategoryGroup = (e: React.DragEvent) => {
    e.preventDefault();
    setDropZoneActive(false);
    setDropInsertIndex(null);
    if (draggedIndex === null && draggedShop) {
      handleDropFromRight(0, e);
    }
  };

  const handleDropOnSlot = (insertIndex: number) => (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDropZoneActive(false);
    setDropInsertIndex(null);
    if (draggedIndex === null) {
      handleDropFromRight(insertIndex, e);
    } else {
      handleDropReorder(insertIndex)(e);
    }
  };

  const handleDropReorder = (slotIndex: number) => async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDropInsertIndex(null);

    if (!selectedCategoryGroupCode || draggedIndex === null || !projectDetail) return;
    if (slotIndex === draggedIndex) return;

    const projectCode = projectDetail.code || getProjectCode();
    if (!projectCode || !draggedShop) return;

    const treeCode = getTreeCode();
    if (!treeCode) return;

    const currentShops = [...(categoryGroupShops.get(selectedCategoryGroupCode) || [])];
    const [removed] = currentShops.splice(draggedIndex, 1);
    const insertIndex = slotIndex > draggedIndex ? slotIndex - 1 : slotIndex;
    currentShops.splice(insertIndex, 0, removed);

    const prev = insertIndex > 0 ? currentShops[insertIndex - 1] : null;
    const next = insertIndex < currentShops.length - 1 ? currentShops[insertIndex + 1] : null;
    const sortOrder = generateBetween(prev?.sortOrder ?? null, next?.sortOrder ?? null);

    try {
      setApiBusy(true);
      await updateShopInTree(projectCode, removed.shopCode, {
        operation: 'update',
        treeCode,
        sortOrder
      });
      const reordered = currentShops.map((cp, index) => ({
        ...cp,
        order: index,
        sortOrder: cp.shopCode === removed.shopCode ? sortOrder : cp.sortOrder
      }));
      setCategoryGroupShops(prevMap => new Map(prevMap).set(selectedCategoryGroupCode, reordered));
    } catch (err) {
      console.error('순서 변경 실패:', err);
      alert(err instanceof Error ? err.message : '순서 변경에 실패했습니다.');
    } finally {
      setApiBusy(false);
    }
  };

  const handleRemoveShop = async (shopCode: string) => {
    if (!selectedCategoryGroupCode || !projectDetail) return;

    const projectCode = projectDetail.code || getProjectCode();
    if (!projectCode) return;

    const treeCode = getTreeCode();
    if (!treeCode) return;

    try {
      setApiBusy(true);
      await updateShopInTree(projectCode, shopCode, { operation: 'remove', treeCode });
      const currentShops = (categoryGroupShops.get(selectedCategoryGroupCode) || [])
        .filter(cp => cp.shopCode !== shopCode)
        .map((cp, index) => ({ ...cp, order: index }));
      setCategoryGroupShops(prev => new Map(prev).set(selectedCategoryGroupCode, currentShops));
    } catch (err) {
      console.error('매장 제거 실패:', err);
      alert(err instanceof Error ? err.message : '매장 제거에 실패했습니다.');
    } finally {
      setApiBusy(false);
    }
  };

  const ShopCard: React.FC<{
    shop: Shop;
    isDraggable?: boolean;
    showRemove?: boolean;
    onRemove?: () => void;
    onDragStart?: (e: React.DragEvent) => void;
    onDragEnd?: (e: React.DragEvent) => void;
  }> = ({ shop, isDraggable = true, showRemove = false, onRemove, onDragStart, onDragEnd }) => {
    return (
      <div
        draggable={isDraggable}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        className="group bg-white border-2 border-gray-200 rounded-lg p-3 hover:border-indigo-300 hover:shadow-md transition-all cursor-move"
      >
        <div className="flex items-center gap-3">
          {isDraggable && (
            <GripVertical className="w-5 h-5 text-gray-400 flex-shrink-0" />
          )}
          <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <Store className="w-6 h-6 text-gray-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-gray-900 truncate">{shop.name}</p>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-xs text-gray-500 font-mono">{shop.outletCode}</span>
              <span className="text-xs text-gray-400">•</span>
              <span className="text-xs font-medium text-gray-700">{shop.type || '-'}</span>
            </div>
          </div>
          {showRemove && onRemove && (
            <button
              onClick={onRemove}
              className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-600 transition-colors opacity-0 group-hover:opacity-100"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderCategoryGroupTreeItem = (node: CategoryGroupNode, level: number = 0): React.ReactNode => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedCodes.has(node.code);
    const isSelected = selectedCategoryGroupCode === node.code;

    return (
      <div key={node.code}>
        <div
          className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
            isSelected ? 'bg-indigo-100 text-indigo-900 font-medium' : 'hover:bg-gray-100'
          }`}
          style={{ paddingLeft: `${level * 16 + 12}px` }}
          onClick={() => handleCategoryGroupSelect(node.code)}
        >
          {hasChildren && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleCategoryGroupToggle(node.code);
              }}
              className="p-0.5"
            >
              {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
          )}
          {!hasChildren && <div className="w-5" />}
          <FolderTree className="w-4 h-4" />
          <span className="text-sm">{node.title}</span>
          {node.code && (
            <span className="text-xs text-gray-400">{node.code}</span>
          )}
          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
            매장: {!treeCountsLoaded ? '-' : (treeNodeShopCounts[`${categoryGroupCode}:${node.code}`] ?? 0)}
          </span>
          {node.type && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">
              {node.type === 'CATEGORY' ? '카테고리' : '그룹'}
            </span>
          )}
        </div>
        {hasChildren && isExpanded && (
          <div>
            {node.children!.map((child) => renderCategoryGroupTreeItem(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const currentCategoryGroupShops = selectedCategoryGroupCode
    ? (categoryGroupShops.get(selectedCategoryGroupCode) || [])
        .sort((a, b) => a.sortOrder.localeCompare(b.sortOrder))
        .map(cp => allShops.find(s => s.code === cp.shopCode))
        .filter((s): s is Shop => s !== undefined)
    : [];

  if (!projectDetail) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">프로젝트 정보를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      <ProjectSidebar
        isDesktopSidebarOpen={isDesktopSidebarOpen}
        setIsDesktopSidebarOpen={setIsDesktopSidebarOpen}
        isMobileSidebarOpen={isMobileSidebarOpen}
        setIsMobileSidebarOpen={setIsMobileSidebarOpen}
        activeMenu={activeMenu}
        setActiveMenu={setActiveMenu}
        projectDetail={projectDetail}
      />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Topbar
          title="카테고리/그룹 매장 관리"
          subtitle={projectDetail?.name || '프로젝트를 선택해주세요'}
          isMobileSidebarOpen={isMobileSidebarOpen}
          setIsMobileSidebarOpen={setIsMobileSidebarOpen}
          isProfileDropdownOpen={isProfileDropdownOpen}
          setIsProfileDropdownOpen={setIsProfileDropdownOpen}
          showMobileLogo={true}
          projectDetail={projectDetail}
        />

        <main className="flex-1 flex flex-col min-h-0 p-4 sm:p-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6 flex-shrink-0">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
              <div className="flex items-center gap-4 flex-1">
                {selectedCategoryGroup && (
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center overflow-hidden">
                      <FolderTree className="w-5 h-5 text-gray-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{selectedCategoryGroup.title}</h3>
                      <p className="text-xs text-gray-500">{selectedCategoryGroup.brandName ? `${selectedCategoryGroup.brandName} (${selectedCategoryGroup.code})` : selectedCategoryGroup.code}</p>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigate('/shops/category-groups')}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors border border-gray-300"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>목록으로</span>
                </button>
              </div>
            </div>
            {apiBusy && (
              <div className="mt-3 text-sm text-gray-500 flex items-center gap-2">
                <span className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent" />
                저장 중…
              </div>
            )}
          </div>

          <div className="flex-1 min-h-0 grid grid-cols-12 gap-4">
            <div className="col-span-3 bg-white rounded-lg border-2 border-gray-200 overflow-hidden flex flex-col">
              <div className="p-4 border-b border-gray-200">
                <h3 className="font-semibold text-gray-900">카테고리/그룹</h3>
                <p className="text-xs text-gray-500 mt-1">
                  하위 카테고리/그룹
                </p>
              </div>
              <div className="flex-1 overflow-y-auto p-3">
                {isLoadingTree ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : treeData.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-20">
                    <FolderTree className="w-16 h-16 text-gray-300 mb-4" />
                    <p className="text-gray-500 text-center px-4">하위 카테고리/그룹이 없습니다.</p>
                  </div>
                ) : (
                  treeData.map((node) => renderCategoryGroupTreeItem(node))
                )}
              </div>
            </div>

            <div className="col-span-5 bg-white rounded-lg border-2 border-gray-200 overflow-hidden flex flex-col">
              <div className="p-4 border-b border-gray-200">
                <h3 className="font-semibold text-gray-900">
                  {selectedCategoryGroupCode
                    ? `카테고리/그룹 매장 (${currentCategoryGroupShops.length})`
                    : '카테고리/그룹을 선택하세요'}
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  드래그하여 순서 변경 또는 오른쪽에서 추가
                </p>
              </div>
              <div
                className={`flex-1 overflow-y-auto p-4 space-y-2 ${
                  dropZoneActive ? 'bg-indigo-50 border-4 border-dashed border-indigo-300' : ''
                }`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDropZoneActive(true);
                }}
                onDragLeave={(e) => {
                  if (!e.currentTarget.contains((e.relatedTarget as Node) || null)) {
                    setDropInsertIndex(null);
                    setDropZoneActive(false);
                  }
                }}
                onDrop={handleDropToCategoryGroup}
              >
                {selectedCategoryGroupCode ? (
                  isLoadingNodeShops ? (
                    <div className="flex justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                    </div>
                  ) : currentCategoryGroupShops.length > 0 ? (
                    <div className="flex flex-col gap-0">
                      {currentCategoryGroupShops.map((shop, index) => (
                        <React.Fragment key={shop.code}>
                          <div
                            className={`min-h-[12px] flex-shrink-0 rounded transition-colors ${
                              dropInsertIndex === index
                                ? 'bg-indigo-200 border-2 border-indigo-400 border-dashed'
                                : 'hover:bg-indigo-50/50'
                            }`}
                            onDragOver={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setDropInsertIndex(index);
                              setDropZoneActive(true);
                            }}
                            onDrop={handleDropOnSlot(index)}
                          />
                          <div
                            onDragOver={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setDropInsertIndex(index);
                              setDropZoneActive(true);
                            }}
                            onDrop={handleDropOnSlot(index)}
                          >
                            <ShopCard
                              shop={shop}
                              showRemove
                              onRemove={() => handleRemoveShop(shop.code)}
                              onDragStart={handleDragStartFromCategory(shop, index)}
                              onDragEnd={handleDragEnd}
                            />
                          </div>
                        </React.Fragment>
                      ))}
                      <div
                        className={`min-h-[12px] flex-shrink-0 rounded transition-colors ${
                          dropInsertIndex === currentCategoryGroupShops.length
                            ? 'bg-indigo-200 border-2 border-indigo-400 border-dashed'
                            : 'hover:bg-indigo-50/50'
                        }`}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setDropInsertIndex(currentCategoryGroupShops.length);
                          setDropZoneActive(true);
                        }}
                        onDrop={handleDropOnSlot(currentCategoryGroupShops.length)}
                      />
                    </div>
                  ) : (
                    <div
                      className="flex flex-col items-center justify-center py-20"
                      onDragOver={(e) => {
                        e.preventDefault();
                        setDropZoneActive(true);
                      }}
                      onDragLeave={() => setDropZoneActive(false)}
                      onDrop={handleDropToCategoryGroup}
                    >
                      <Store className="w-16 h-16 text-gray-300 mb-4" />
                      <p className="text-gray-500 text-center px-4">
                        오른쪽에서 매장을 드래그하여 추가하세요
                      </p>
                    </div>
                  )
                ) : (
                  <div className="flex flex-col items-center justify-center py-20">
                    <FolderTree className="w-16 h-16 text-gray-300 mb-4" />
                    <p className="text-gray-500 text-center px-4">카테고리/그룹을 선택하세요</p>
                  </div>
                )}
              </div>
            </div>

            <div className="col-span-4 bg-white rounded-lg border-2 border-gray-200 overflow-hidden flex flex-col">
              <div className="p-4 border-b border-gray-200">
                <h3 className="font-semibold text-gray-900">
                  전체 매장 ({allShops.length})
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  드래그하여 카테고리/그룹에 추가
                </p>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-2">
                {isLoadingShops ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : shopListError ? (
                  <div className="flex flex-col items-center justify-center py-12 px-4">
                    <p className="text-red-600 text-sm mb-4 text-center">{shopListError}</p>
                    <button
                      onClick={() => {
                        setShopListError(null);
                      }}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      다시 시도
                    </button>
                  </div>
                ) : allShops.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-20">
                    <Store className="w-16 h-16 text-gray-300 mb-4" />
                    <p className="text-gray-500 text-center px-4">
                      매장이 없습니다.
                    </p>
                  </div>
                ) : (
                  allShops.map((shop) => (
                    <ShopCard
                      key={shop.code}
                      shop={shop}
                      onDragStart={handleDragStartFromAll(shop)}
                      onDragEnd={handleDragEnd}
                    />
                  ))
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ShopCategoryGroupShopConfig;
